// Utility functions for Team Manager filtering and sorting logic

// Determine if a member has access to a given venue ownership
export const memberHasVenueAccess = (member, venueOwnershipId) => {
  if (!venueOwnershipId || !member) return false;

  // Members with all-venue access are considered to have access to every venue
  if (member.all_venue_access) return true;

  const accesses = member.venue_ownership_accesses || [];
  return accesses.some((voa) => {
    const idFromAssoc = voa.venue_ownership?.id;
    const idFromField = voa.venue_ownership_id;
    return idFromAssoc === venueOwnershipId || idFromField === venueOwnershipId;
  });
};

// Compute mapping of role_id -> count of members with that role
export const computeMembersByRole = (allMembers) => {
  const counts = {};

  (allMembers || []).forEach((member) => {
    if (member.role?.id) {
      counts[member.role.id] = (counts[member.role.id] || 0) + 1;
    }
  });

  return counts;
};

// Compute mapping of venueOwnership.id -> count of members with access
export const computeMembersByVenue = (allMembers, venueOwnerships) => {
  const counts = {};
  const members = allMembers || [];
  const venues = venueOwnerships || [];

  venues.forEach((venueOwnership) => {
    const ownershipId = venueOwnership.id;
    counts[ownershipId] = members.reduce((acc, member) => (
      memberHasVenueAccess(member, ownershipId) ? acc + 1 : acc
    ), 0);
  });

  return counts;
};

export const computeMembersByTeamFunction = (allMembers) => {
  const counts = {};

  (allMembers || []).forEach((member) => {
    (member.team_function_ids || []).forEach((id) => {
      counts[id] = (counts[id] || 0) + 1;
    });
  });

  return counts;
};

// Filter and sort members based on search, role filter, venue filter, and sort state
export const filterAndSortMembers = ({
  allMembers = [],
  roles = [],
  searchTerm = '',
  roleFilter = [],
  venueFilter = [],
  teamFunctionFilter = [],
  sortBy = 'name',
  sortDirection = 'asc'
}) => {
  const members = allMembers || [];

  const guestRole = roles.find((role) => role.display_name === 'Guest' || role.name === 'Guest');
  const hasRoleFilter = Array.isArray(roleFilter) && roleFilter.length > 0;
  const hasVenueFilter = Array.isArray(venueFilter) && venueFilter.length > 0;
  const hasTeamFunctionFilter = Array.isArray(teamFunctionFilter) && teamFunctionFilter.length > 0;

  let filtered = members;

  // By default, hide members with the Guest role unless the user explicitly filters to Guest
  if (guestRole && (!hasRoleFilter || !roleFilter.includes(guestRole.id))) {
    filtered = filtered.filter((member) => member.role?.id !== guestRole.id);
  }

  // Apply search filter (name or email)
  if (searchTerm) {
    const searchLower = searchTerm.toLowerCase();
    filtered = filtered.filter((member) => {
      const name = `${member.first_name || ''} ${member.last_name || ''}`.toLowerCase();
      const email = (member.email || '').toLowerCase();
      return name.includes(searchLower) || email.includes(searchLower);
    });
  }

  // Apply role filter (multi-select)
  if (hasRoleFilter) {
    filtered = filtered.filter((member) => (
      member.role?.id && roleFilter.includes(member.role.id)
    ));
  }

  // Apply venue access filter (multi-select; OR across venues)
  if (hasVenueFilter) {
    filtered = filtered.filter((member) => (
      venueFilter.some((venueOwnershipId) =>
        memberHasVenueAccess(member, venueOwnershipId)
      )
    ));
  }

  // Apply team function filter (multi-select; OR across functions)
  if (hasTeamFunctionFilter) {
    filtered = filtered.filter((member) => {
      const ids = member.team_function_ids || [];
      return ids.some((id) => teamFunctionFilter.includes(id));
    });
  }

  // Sorting helpers
  const direction = sortDirection === 'desc' ? -1 : 1;

  const getName = (member) => {
    const fullName = `${member.first_name || ''} ${member.last_name || ''}`.trim();
    if (fullName) return fullName.toLowerCase();
    if (member.name) return member.name.toLowerCase();
    return (member.email || '').toLowerCase();
  };

  const getRoleName = (member) => (
    (member.role?.display_name || '').toLowerCase()
  );

  const getTeamFunctionName = (member) => {
    const fns = member.team_functions || [];
    if (fns.length === 0) return '';
    const names = fns.map((fn) => (fn.name || '').toLowerCase()).filter(Boolean).sort();
    return names[0] || '';
  };

  const getVenueLabel = (member) => {
    if (member.all_venue_access) return 'all venues';

    const accesses = member.venue_ownership_accesses || [];
    if (accesses.length === 0) return '';

    const names = accesses.map((voa) => voa.venue_ownership?.venueable?.name || '');
    return names.filter(Boolean).sort()[0].toLowerCase();
  };

  const sorted = [...filtered].sort((a, b) => {
    let aVal = '';
    let bVal = '';

    if (sortBy === 'name') {
      aVal = getName(a);
      bVal = getName(b);
    } else if (sortBy === 'role') {
      aVal = getRoleName(a);
      bVal = getRoleName(b);
    } else if (sortBy === 'function') {
      aVal = getTeamFunctionName(a);
      bVal = getTeamFunctionName(b);
    } else if (sortBy === 'venue') {
      aVal = getVenueLabel(a);
      bVal = getVenueLabel(b);
    }

    if (aVal === bVal) return 0;
    return aVal > bVal ? direction : -direction;
  });

  return sorted;
};

export const isProtectedTeamOwner = (member, team) =>
  member.type_for_ui === 'TeamMembership' &&
  !!member.owner &&
  !!(team && team.setup_complete);
